/**
 * @fileoverview Favorite-cases store for case/file resources.
 *
 * A tiny localStorage-backed set of favorited case ids, exposed via
 * `useSyncExternalStore` so every consumer (the Files Favorites filter, each
 * row's star toggle, and the workspace rail's Pinned section) stays in sync
 * without prop-threading. Mirrors the favorite-apps store pattern in
 * `favorite-apps.ts`.
 *
 * Favorites are user-driven and INDEPENDENT of `case.flagged` — starring a case
 * here does not flag it, and a flagged case is not implicitly favorited. The
 * store starts empty; it is never auto-seeded from flagged cases.
 */

import { useCallback, useSyncExternalStore } from "react";

import { api } from "./api";

const FAVORITE_CASES_KEY = "ic.shell.favoriteCases";
const RESOURCE_TYPE = "case";

function readFavorites(): string[] {
  try {
    const raw = window.localStorage.getItem(FAVORITE_CASES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

let favoritesMemo: string[] = readFavorites();
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

function persist(next: string[]): void {
  favoritesMemo = next;
  try {
    window.localStorage.setItem(FAVORITE_CASES_KEY, JSON.stringify(next));
  } catch {
    // ignore persistence failure — in-memory state still updates
  }
  emit();
}

/**
 * Replace the store with the server's favorite set and refresh the localStorage
 * cache. Called once on boot by favorites-sync after the auth token resolves;
 * the server is the source of truth from that point on.
 */
export function seedFromServer(ids: string[]): void {
  persist([...ids]);
}

/**
 * Optimistic toggle: update the UI + localStorage immediately, then sync the
 * server. On API failure, REVERT to the prior set so the UI never lies about
 * persisted state. Module-level so the closure always reads live state.
 */
function toggleFavoriteImpl(id: string): void {
  const prior = favoritesMemo;
  const next = prior.includes(id) ? prior.filter((x) => x !== id) : [...prior, id];
  persist(next);
  const request = next.includes(id)
    ? api.addFavorite(RESOURCE_TYPE, id)
    : api.removeFavorite(RESOURCE_TYPE, id);
  void request.catch(() => {
    persist(prior);
  });
}

export function useFavoriteCases(): {
  favorites: ReadonlySet<string>;
  isFavorite: (id: string) => boolean;
  toggleFavorite: (id: string) => void;
} {
  const subscribe = (cb: () => void) => {
    listeners.add(cb);
    return () => listeners.delete(cb);
  };
  const ids = useSyncExternalStore(subscribe, () => favoritesMemo, () => favoritesMemo);
  const favorites = new Set(ids);

  const isFavorite = useCallback((id: string) => favoritesMemo.includes(id), []);
  const toggleFavorite = useCallback((id: string) => toggleFavoriteImpl(id), []);

  return { favorites, isFavorite, toggleFavorite };
}
